import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, MapPin, Clock, CheckCircle, Shield, Zap, Users, ArrowRight, Star } from "lucide-react";
import { Header, Footer, StickyCTA, QuoteForm } from "../components/Layout";
import seoData from "../data/seoVilles.json";

// Page générique pour les communes sans contenu dédié (évite les soft 404)
function buildGenericVille(v) {
  return {
    nom: v.nom,
    region: `${v.region} (${v.dept})`,
    code: v.cp,
    accroche: `Cafards, rats ou punaises de lit à ${v.nom} ? On intervient rapidement, souvent le jour même.`,
    description: `À ${v.nom} (${v.cp}), comme partout ${v.dept === "06" ? "dans les Alpes-Maritimes" : `en ${v.region}`}, les nuisibles ne préviennent pas. Rats dans le garage, cafards dans la cuisine, nid de guêpes sous le toit... Depuis 2015, Azur Tech intervient à ${v.nom} et dans les communes voisines avec la même exigence : intervention rapide, tarif honnête et résultat garanti. Devis gratuit par téléphone.`,
    metaDescription: `Désinsectisation et dératisation à ${v.nom} (${v.cp}). Cafards, rats, punaises de lit, guêpes traités rapidement. Intervention 24h/24, devis gratuit ☎ 07 64 07 95 35`,
    quartiers: v.quartiers || ["Centre-ville", "Quartiers résidentiels", "Zones commerciales", "Environs"],
    problemes: [
      `Cafards et blattes dans les logements de ${v.nom}`,
      "Rats et souris dans les caves, garages et jardins",
      "Punaises de lit dans les chambres et locations",
      "Nids de guêpes et frelons dès le printemps",
    ],
  };
}

// Données des villes - ton professionnel, local, humain
const VILLES = {
  nice: {
    nom: "Nice",
    region: "Alpes-Maritimes (06)",
    code: "06000",
    accroche: "Cafards, rats ou punaises de lit à Nice ? On intervient chez vous aujourd'hui.",
    description: "À Nice, les nuisibles adorent le climat doux autant que nous. Entre le Vieux-Nice et les quartiers résidentiels, on connaît chaque recoin où ils se cachent. Depuis 2015, on aide les Niçois à retrouver un logement sain, sans stress.",
    metaDescription: "Désinsectisation et dératisation à Nice (06). Cafards, rats, punaises de lit traités rapidement. Intervention urgence 24h/24. Devis gratuit ☎ 07 64 07 95 35",
    quartiers: ["Vieux-Nice", "Cimiez", "Libération", "Port", "Riquier", "Gambetta", "Magnan", "Arenas"],
    problemes: ["Cafards dans les immeubles anciens du centre", "Rats près des restaurants et commerces", "Punaises de lit dans les locations Airbnb", "Guêpes et frelons dès le printemps"],
  },
  cannes: {
    nom: "Cannes",
    region: "Alpes-Maritimes (06)",
    code: "06400",
    accroche: "Problème de nuisibles à Cannes ? Intervention discrète, résultat garanti.",
    description: "À Cannes, la discrétion compte autant que l'efficacité. Que vous soyez hôtelier sur la Croisette ou particulier à la Bocca, on intervient rapidement et sans bruit. Nos techniciens connaissent bien la ville et ses problématiques spécifiques.",
    metaDescription: "Dératisation et désinsectisation à Cannes (06). Traitement discret cafards, rats, punaises de lit. Intervention rapide hôtels et particuliers ☎ 07 64 07 95 35",
    quartiers: ["La Croisette", "Le Suquet", "Palm Beach", "La Bocca", "Carnot", "Californie"],
    problemes: ["Punaises de lit dans les hôtels et locations", "Cafards dans les cuisines de restaurants", "Rats près des zones portuaires", "Guêpes autour des piscines et terrasses"],
  },
  antibes: {
    nom: "Antibes",
    region: "Alpes-Maritimes (06)",
    code: "06600",
    accroche: "Nuisibles à Antibes ou Juan-les-Pins ? On s'en occupe rapidement.",
    description: "Antibes et Juan-les-Pins, c'est notre terrain depuis des années. Du vieil Antibes au Cap, en passant par le port Vauban, on intervient vite et bien. Particuliers, restaurateurs, gérants de locations : on a l'habitude de vos besoins.",
    metaDescription: "Expert anti-nuisibles Antibes Juan-les-Pins (06). Élimination cafards, rats, punaises de lit. Intervention rapide port Vauban, Cap d'Antibes ☎ 07 64 07 95 35",
    quartiers: ["Vieil Antibes", "Juan-les-Pins", "Cap d'Antibes", "La Fontonne", "Port Vauban"],
    problemes: ["Cafards dans les commerces du port", "Punaises de lit en saison touristique", "Rats près des marchés", "Fourmis dans les villas du Cap"],
  },
  menton: {
    nom: "Menton",
    region: "Alpes-Maritimes (06)",
    code: "06500",
    accroche: "Rats, cafards ou punaises à Menton ? On intervient même le week-end.",
    description: "Menton et son micro-climat attirent malheureusement aussi les nuisibles. Dans la vieille ville comme dans les quartiers résidentiels, on connaît les problèmes locaux. Intervention rapide, tarif honnête, résultat garanti.",
    metaDescription: "Désinsectisation dératisation Menton (06). Traitement cafards, rats, punaises de lit. Service 24h/24 week-end inclus. Devis gratuit ☎ 07 64 07 95 35",
    quartiers: ["Vieille Ville", "Garavan", "Careï", "Madone", "Borrigo"],
    problemes: ["Cafards attirés par le climat doux", "Rats dans les jardins et oliveraies", "Punaises de lit dans les résidences", "Fourmis envahissantes en été"],
  },
  grasse: {
    nom: "Grasse",
    region: "Alpes-Maritimes (06)",
    code: "06130",
    accroche: "Besoin d'un dératiseur ou désinsectiseur à Grasse ? Appelez-nous.",
    description: "À Grasse, entre parfumeries et vieilles pierres, les nuisibles trouvent facilement refuge. On intervient dans toute la commune : centre historique, Plan de Grasse, Magagnosc... Devis gratuit, intervention rapide.",
    metaDescription: "Dératiseur désinsectiseur Grasse (06). Élimination rats, cafards, guêpes. Intervention centre historique et tous quartiers. Devis gratuit ☎ 07 64 07 95 35",
    quartiers: ["Centre historique", "Plan de Grasse", "Magagnosc", "Saint-Claude", "Plascassier"],
    problemes: ["Rats dans les vieux bâtiments", "Cafards dans les commerces", "Guêpes et frelons au printemps", "Fourmis dans les jardins"],
  },
  cagnes_sur_mer: {
    nom: "Cagnes-sur-Mer",
    region: "Alpes-Maritimes (06)",
    code: "06800",
    accroche: "Cafards ou rats à Cagnes-sur-Mer ? Intervention le jour même.",
    description: "Du Cros-de-Cagnes au Haut-de-Cagnes, on connaît bien cette ville. Les immeubles en bord de mer, les villas sur les hauteurs : chaque quartier a ses problématiques. On s'adapte et on règle le problème.",
    metaDescription: "Désinsectisation dératisation Cagnes-sur-Mer (06). Cafards, rats, punaises traités le jour même. Cros-de-Cagnes, Haut-de-Cagnes ☎ 07 64 07 95 35",
    quartiers: ["Cros-de-Cagnes", "Haut-de-Cagnes", "Le Logis", "Val Fleuri"],
    problemes: ["Cafards dans les immeubles côtiers", "Rats près des restaurants", "Moustiques tigres en été", "Punaises de lit"],
  },
  saint_raphael: {
    nom: "Saint-Raphaël",
    region: "Var (83)",
    code: "83700",
    accroche: "Nuisibles à Saint-Raphaël ? Intervention rapide et discrète.",
    description: "Saint-Raphaël et ses environs (Agay, Boulouris, Valescure) méritent des professionnels locaux. On intervient vite, on travaille proprement, et on garantit le résultat. Hôteliers et particuliers nous font confiance.",
    metaDescription: "Expert anti-nuisibles Saint-Raphaël Var (83). Traitement cafards, rats, punaises de lit. Agay, Boulouris, Valescure. Devis gratuit ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Valescure", "Boulouris", "Agay", "Santa Lucia"],
    problemes: ["Punaises de lit dans les hôtels", "Cafards en cuisine", "Rats autour des poubelles", "Guêpes en été"],
  },
  frejus: {
    nom: "Fréjus",
    region: "Var (83)",
    code: "83600",
    accroche: "Dératisation ou désinsectisation à Fréjus ? On arrive vite.",
    description: "Fréjus, Port-Fréjus, Saint-Aygulf : on couvre tout le secteur. Les campings, les résidences de vacances, les particuliers... On sait s'adapter à chaque situation. Tarifs clairs, pas de surprise.",
    metaDescription: "Dératisation désinsectisation Fréjus Var (83). Cafards, rats, punaises éliminés. Port-Fréjus, Saint-Aygulf couverts. Tarif clair ☎ 07 64 07 95 35",
    quartiers: ["Centre historique", "Fréjus Plage", "Port-Fréjus", "Saint-Aygulf"],
    problemes: ["Moustiques tigres en été", "Cafards dans les campings", "Rats près des commerces", "Punaises de lit"],
  },
  mandelieu: {
    nom: "Mandelieu-la-Napoule",
    region: "Alpes-Maritimes (06)",
    code: "06210",
    accroche: "Problème de nuisibles à Mandelieu ? Devis gratuit, intervention rapide.",
    description: "Entre le port de la Napoule et les quartiers résidentiels, Mandelieu a ses spécificités. On les connaît et on s'y adapte. Intervention discrète pour les résidences de standing, efficace pour tous.",
    metaDescription: "Désinsectisation dératisation Mandelieu-la-Napoule (06). Traitement discret cafards, rats. Port de la Napoule et résidences ☎ 07 64 07 95 35",
    quartiers: ["La Napoule", "Capitou", "Minelle", "Cannes-Marina"],
    problemes: ["Cafards dans les résidences", "Rats près du port", "Guêpes autour des golfs", "Punaises de lit"],
  },
  vallauris: {
    nom: "Vallauris",
    region: "Alpes-Maritimes (06)",
    code: "06220",
    accroche: "Cafards ou rats à Vallauris ou Golfe-Juan ? On intervient aujourd'hui.",
    description: "Vallauris, Golfe-Juan, Super Cannes : on couvre tout le secteur. La céramique, c'est votre spécialité. Les nuisibles, c'est la nôtre. Appelez-nous pour un devis gratuit.",
    metaDescription: "Expert nuisibles Vallauris Golfe-Juan (06). Élimination cafards, rats, punaises de lit. Super Cannes couvert. Devis gratuit ☎ 07 64 07 95 35",
    quartiers: ["Centre Vallauris", "Golfe-Juan", "Super Cannes", "Les Encourdoules"],
    problemes: ["Cafards dans les ateliers", "Punaises à Golfe-Juan", "Rats dans les vieux quartiers", "Fourmis"],
  },
  mougins: {
    nom: "Mougins",
    region: "Alpes-Maritimes (06)",
    code: "06250",
    accroche: "Nuisibles à Mougins ? Intervention discrète dans votre villa.",
    description: "À Mougins, on comprend l'importance de la discrétion. Villas, restaurants gastronomiques, hôtels de charme : on intervient avec professionnalisme et sans bruit. Votre tranquillité est notre priorité.",
    metaDescription: "Dératisation désinsectisation Mougins (06). Intervention discrète villas et restaurants gastronomiques. Service haut de gamme ☎ 07 64 07 95 35",
    quartiers: ["Vieux Mougins", "Mougins-le-Haut", "Font de l'Orme", "Tournamy"],
    problemes: ["Rats dans les jardins", "Guêpes près des piscines", "Fourmis dans les villas", "Cafards en cuisine"],
  },
  vence: {
    nom: "Vence",
    region: "Alpes-Maritimes (06)",
    code: "06140",
    accroche: "Cafards ou rats à Vence ? On intervient rapidement dans l'arrière-pays niçois.",
    description: "Vence, entre mer et montagne, a des problématiques de nuisibles bien particulières. Les vieilles maisons du centre et les villas en périphérie : on connaît et on intervient vite.",
    metaDescription: "Désinsectisation dératisation Vence arrière-pays niçois (06). Cafards, rats, guêpes traités. Centre historique et environs ☎ 07 64 07 95 35",
    quartiers: ["Centre historique", "La Sine", "Saint-Martin", "Les Parcs"],
    problemes: ["Rats dans les vieilles maisons", "Guêpes et frelons au printemps", "Fourmis dans les jardins", "Cafards"],
  },
  saint_laurent_du_var: {
    nom: "Saint-Laurent-du-Var",
    region: "Alpes-Maritimes (06)",
    code: "06700",
    accroche: "Nuisibles à Saint-Laurent-du-Var ? Intervention rapide près de l'aéroport.",
    description: "Saint-Laurent-du-Var, entre le Var et la mer. Les résidences, les commerces du Cap 3000 : on intervient partout avec le même professionnalisme. Devis gratuit.",
    metaDescription: "Expert anti-nuisibles Saint-Laurent-du-Var (06). Cafards, rats, punaises éliminés. Cap 3000 et résidences couverts ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Vespins", "Montaleigne", "Cap 3000"],
    problemes: ["Cafards dans les immeubles", "Rats près des restaurants", "Punaises de lit", "Guêpes"],
  },
  carros: {
    nom: "Carros",
    region: "Alpes-Maritimes (06)",
    code: "06510",
    accroche: "Dératisation ou désinsectisation à Carros ? On arrive vite.",
    description: "Carros, son village perché et sa zone industrielle. Deux mondes différents mais les mêmes nuisibles. On intervient dans tout Carros et ses environs.",
    metaDescription: "Dératisation désinsectisation Carros (06). Traitement rats, cafards zone industrielle et village. Intervention rapide ☎ 07 64 07 95 35",
    quartiers: ["Carros Village", "Carros Le Neuf", "Zone industrielle", "Les Plans"],
    problemes: ["Rats dans les entrepôts", "Cafards dans les commerces", "Guêpes dans les bureaux", "Souris"],
  },
  le_cannet: {
    nom: "Le Cannet",
    region: "Alpes-Maritimes (06)",
    code: "06110",
    accroche: "Cafards ou punaises de lit au Cannet ? On s'en occupe.",
    description: "Le Cannet, juste au-dessus de Cannes. Villas résidentielles et appartements : on intervient rapidement et discrètement dans toute la commune.",
    metaDescription: "Désinsectisation dératisation Le Cannet (06). Punaises de lit, cafards, rats traités. Villas et appartements couverts ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Rocheville", "Les Bruyères", "Aubarède"],
    problemes: ["Punaises de lit", "Cafards dans les cuisines", "Rats dans les jardins", "Fourmis"],
  },
  beausoleil: {
    nom: "Beausoleil",
    region: "Alpes-Maritimes (06)",
    code: "06240",
    accroche: "Nuisibles à Beausoleil ou Monaco ? Intervention discrète et rapide.",
    description: "Beausoleil, aux portes de Monaco. La discrétion est de mise. On intervient avec professionnalisme dans les résidences et commerces.",
    metaDescription: "Expert nuisibles Beausoleil Monaco (06). Traitement discret cafards, punaises, rats. Intervention professionnelle ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Riviera", "Ténao", "Monte-Carlo adjacents"],
    problemes: ["Cafards dans les immeubles", "Punaises de lit", "Rats", "Fourmis"],
  },
  villefranche_sur_mer: {
    nom: "Villefranche-sur-Mer",
    region: "Alpes-Maritimes (06)",
    code: "06230",
    accroche: "Rats ou cafards à Villefranche-sur-Mer ? On intervient le jour même.",
    description: "Villefranche-sur-Mer et sa magnifique rade. Les restaurants du port, les villas sur les hauteurs : on connaît la ville et ses spécificités.",
    metaDescription: "Dératisation désinsectisation Villefranche-sur-Mer (06). Rats, cafards traités le jour même. Port et villas couverts ☎ 07 64 07 95 35",
    quartiers: ["Vieille ville", "Port", "Beaulieu", "Les hauteurs"],
    problemes: ["Rats près du port", "Cafards dans les restaurants", "Guêpes", "Punaises de lit"],
  },
  roquebrune_cap_martin: {
    nom: "Roquebrune-Cap-Martin",
    region: "Alpes-Maritimes (06)",
    code: "06190",
    accroche: "Nuisibles à Roquebrune ou Cap-Martin ? Intervention discrète garantie.",
    description: "Roquebrune-Cap-Martin, entre Monaco et Menton. Villas de luxe, résidences et commerces : on intervient avec discrétion et professionnalisme.",
    metaDescription: "Expert anti-nuisibles Roquebrune-Cap-Martin (06). Intervention discrète villas de luxe. Rats, cafards, guêpes traités ☎ 07 64 07 95 35",
    quartiers: ["Roquebrune village", "Cap-Martin", "Carnolès", "Bon Voyage"],
    problemes: ["Rats dans les jardins", "Guêpes et frelons", "Punaises de lit", "Cafards"],
  },
  la_trinite: {
    nom: "La Trinité",
    region: "Alpes-Maritimes (06)",
    code: "06340",
    accroche: "Cafards ou rats à La Trinité ? Votre expert local intervient vite.",
    description: "La Trinité, aux portes de Nice dans la vallée du Paillon. On connaît bien le secteur et on intervient rapidement pour tous vos problèmes de nuisibles.",
    metaDescription: "Désinsectisation dératisation La Trinité vallée du Paillon (06). Cafards, rats, souris éliminés rapidement ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Les Music", "La Condamine", "Laghet"],
    problemes: ["Souris dans les maisons", "Cafards", "Guêpes", "Rats"],
  },
  biot: {
    nom: "Biot",
    region: "Alpes-Maritimes (06)",
    code: "06410",
    accroche: "Nuisibles à Biot ? Intervention rapide dans le village et Sophia Antipolis.",
    description: "Biot, entre le village historique des verriers et le technopole Sophia Antipolis. On intervient aussi bien chez les particuliers que dans les entreprises.",
    metaDescription: "Dératisation désinsectisation Biot Sophia Antipolis (06). Traitement rats, cafards entreprises et particuliers ☎ 07 64 07 95 35",
    quartiers: ["Village", "Sophia Antipolis", "Les Combes", "La Brague"],
    problemes: ["Rats dans les bureaux", "Cafards", "Guêpes dans les jardins", "Fourmis"],
  },
  eze: {
    nom: "Èze",
    region: "Alpes-Maritimes (06)",
    code: "06360",
    accroche: "Guêpes ou rats à Èze ? Intervention discrète du village à la plage.",
    description: "Èze, du village perché à Èze-Bord-de-Mer. Hôtels de luxe, villas et résidences : on intervient avec la plus grande discrétion.",
    metaDescription: "Expert nuisibles Èze village et bord de mer (06). Intervention discrète hôtels de luxe, villas. Guêpes, rats traités ☎ 07 64 07 95 35",
    quartiers: ["Èze Village", "Èze-Bord-de-Mer", "Grande Corniche", "Moyenne Corniche"],
    problemes: ["Guêpes près des terrasses", "Rats", "Punaises de lit dans les hôtels", "Cafards"],
  },
  // Île-de-France
  paris: {
    nom: "Paris",
    region: "Île-de-France (75)",
    code: "75000",
    accroche: "Punaises de lit, rats ou cafards à Paris ? Intervention sous 24h.",
    description: "Paris et ses 20 arrondissements, on connaît. Immeubles haussmanniens, studios, restaurants, hôtels... Chaque situation est différente, mais notre objectif reste le même : vous débarrasser des nuisibles rapidement et durablement.",
    metaDescription: "Désinsectisation dératisation Paris tous arrondissements. Punaises de lit, rats, cafards. Intervention urgence 24h ☎ 07 64 07 95 35",
    quartiers: ["Tous les arrondissements", "Le Marais", "Montmartre", "Bastille", "Nation", "Belleville"],
    problemes: ["Punaises de lit (fléau parisien)", "Rats dans les caves et parkings", "Cafards dans les cuisines", "Souris dans les commerces"],
  },
  boulogne: {
    nom: "Boulogne-Billancourt",
    region: "Hauts-de-Seine (92)",
    code: "92100",
    accroche: "Rats ou cafards à Boulogne ? On intervient rapidement.",
    description: "Boulogne-Billancourt, entre résidences et bureaux, on connaît. Les parkings souterrains, les immeubles, les commerces : on sait où chercher et comment traiter. Résultat garanti.",
    metaDescription: "Expert anti-nuisibles Boulogne-Billancourt (92). Rats, cafards, punaises traités. Parkings et immeubles couverts ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Pont de Sèvres", "Billancourt", "Les Princes"],
    problemes: ["Rats dans les parkings", "Cafards dans les immeubles", "Punaises de lit", "Souris dans les bureaux"],
  },
  versailles: {
    nom: "Versailles",
    region: "Yvelines (78)",
    code: "78000",
    accroche: "Nuisibles à Versailles ? Intervention discrète et professionnelle.",
    description: "À Versailles, le standing compte. On intervient avec discrétion dans les maisons de ville, les appartements et les commerces. Devis clair, travail soigné, résultat garanti.",
    metaDescription: "Dératisation désinsectisation Versailles (78). Intervention discrète maisons de ville. Rats, souris, guêpes traités ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Notre-Dame", "Montreuil", "Saint-Louis", "Chantiers"],
    problemes: ["Rats dans les jardins", "Souris dans les maisons", "Guêpes et frelons", "Cafards"],
  },
  creteil: {
    nom: "Créteil",
    region: "Val-de-Marne (94)",
    code: "94000",
    accroche: "Cafards ou rats à Créteil ? Intervention rapide, prix honnête.",
    description: "Créteil et ses grands ensembles, on connaît les problématiques. Mont-Mesly, Lac, Université : on intervient partout. Pas de blabla, juste du résultat.",
    metaDescription: "Désinsectisation dératisation Créteil (94). Cafards, rats dans immeubles et caves. Mont-Mesly, Lac couverts ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Mont-Mesly", "Lac", "Université", "Échat"],
    problemes: ["Cafards dans les immeubles", "Rats dans les caves", "Punaises de lit", "Souris"],
  },
  montreuil: {
    nom: "Montreuil",
    region: "Seine-Saint-Denis (93)",
    code: "93100",
    accroche: "Punaises de lit ou rats à Montreuil ? On s'en occupe.",
    description: "Montreuil, c'est varié : vieux immeubles, ateliers d'artistes, pavillons... On s'adapte à chaque situation. Intervention rapide, tarif clair, travail efficace.",
    metaDescription: "Expert nuisibles Montreuil (93). Punaises de lit, rats, cafards traités. Intervention rapide tous quartiers ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Croix de Chavaux", "La Noue", "Villiers-Barbusse"],
    problemes: ["Punaises de lit (très fréquent)", "Rats dans les cours", "Cafards", "Souris"],
  },
  argenteuil: {
    nom: "Argenteuil",
    region: "Val-d'Oise (95)",
    code: "95100",
    accroche: "Nuisibles à Argenteuil ? Devis gratuit, intervention rapide.",
    description: "Argenteuil, en bord de Seine, a ses spécificités. On les connaît et on intervient efficacement dans tous les quartiers. Particuliers et pros, même service.",
    metaDescription: "Dératisation désinsectisation Argenteuil (95). Rats, cafards, punaises éliminés. Bord de Seine et tous quartiers ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Val d'Argent", "Orgemont", "Coteaux"],
    problemes: ["Rats près de la Seine", "Cafards dans les immeubles", "Punaises de lit", "Souris"],
  },
  nanterre: {
    nom: "Nanterre",
    region: "Hauts-de-Seine (92)",
    code: "92000",
    accroche: "Dératisation ou désinsectisation à Nanterre ? On arrive vite.",
    description: "Nanterre, entre La Défense et les quartiers résidentiels, on connaît. Bureaux, résidences, universités : on s'adapte à chaque besoin. Devis gratuit.",
    metaDescription: "Expert anti-nuisibles Nanterre La Défense (92). Rats, cafards bureaux et résidences. Intervention rapide ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Préfecture", "Université", "Mont-Valérien"],
    problemes: ["Rats dans les sous-sols", "Cafards dans les bureaux", "Punaises dans les résidences", "Souris"],
  },
  levallois: {
    nom: "Levallois-Perret",
    region: "Hauts-de-Seine (92)",
    code: "92300",
    accroche: "Cafards ou rats à Levallois ? Intervention discrète et rapide.",
    description: "Levallois, ville dense et dynamique. Immeubles, bureaux, restaurants : on intervient partout avec discrétion. Résultat garanti.",
    metaDescription: "Désinsectisation dératisation Levallois-Perret (92). Cafards, rats traités discrètement. Immeubles et bureaux ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Front de Seine", "Alsace", "Louise Michel"],
    problemes: ["Cafards dans les immeubles", "Rats dans les caves", "Punaises de lit", "Souris"],
  },
  courbevoie: {
    nom: "Courbevoie",
    region: "Hauts-de-Seine (92)",
    code: "92400",
    accroche: "Nuisibles à Courbevoie ou La Défense ? On intervient vite.",
    description: "Courbevoie, entre tours de La Défense et quartiers résidentiels. On connaît les deux mondes et on intervient efficacement dans chacun.",
    metaDescription: "Dératisation désinsectisation Courbevoie La Défense (92). Rats parkings, cafards bureaux traités ☎ 07 64 07 95 35",
    quartiers: ["Bécon", "Charras", "Faubourg de l'Arche", "Centre-ville"],
    problemes: ["Rats dans les parkings", "Cafards", "Punaises dans les hôtels", "Souris"],
  },
  asnieres: {
    nom: "Asnières-sur-Seine",
    region: "Hauts-de-Seine (92)",
    code: "92600",
    accroche: "Rats ou cafards à Asnières ? Intervention le jour même.",
    description: "Asnières, en bord de Seine, avec ses immeubles anciens et ses pavillons. On connaît les problématiques locales et on intervient vite.",
    metaDescription: "Expert nuisibles Asnières-sur-Seine (92). Rats, cafards, punaises traités le jour même. Bord de Seine couvert ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Bac", "Quatre Routes", "Grésillons"],
    problemes: ["Rats près de la Seine", "Cafards dans les vieux immeubles", "Punaises de lit", "Souris"],
  },
  colombes: {
    nom: "Colombes",
    region: "Hauts-de-Seine (92)",
    code: "92700",
    accroche: "Nuisibles à Colombes ? Devis gratuit, résultat garanti.",
    description: "Colombes, ses pavillons et ses immeubles. On intervient dans tous les quartiers avec le même professionnalisme. Tarif honnête, travail soigné.",
    metaDescription: "Désinsectisation dératisation Colombes (92). Rats jardins, cafards, punaises traités. Devis gratuit ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Gare", "Europe", "Petit-Colombes"],
    problemes: ["Rats dans les jardins", "Cafards", "Punaises de lit", "Guêpes"],
  },
  issy: {
    nom: "Issy-les-Moulineaux",
    region: "Hauts-de-Seine (92)",
    code: "92130",
    accroche: "Cafards ou rats à Issy ? Intervention professionnelle et discrète.",
    description: "Issy, ville moderne aux portes de Paris. Bureaux high-tech, résidences récentes : on intervient avec le professionnalisme que vous attendez.",
    metaDescription: "Dératisation désinsectisation Issy-les-Moulineaux (92). Cafards bureaux, rats parkings. Intervention professionnelle ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Val de Seine", "Les Arches", "Corentin Celton"],
    problemes: ["Cafards dans les bureaux", "Rats dans les parkings", "Punaises de lit", "Souris"],
  },
  vincennes: {
    nom: "Vincennes",
    region: "Val-de-Marne (94)",
    code: "94300",
    accroche: "Nuisibles à Vincennes ? Intervention rapide, proche du bois.",
    description: "Vincennes, aux portes du bois. Les rongeurs peuvent venir de là, on le sait. On intervient rapidement pour protéger votre logement.",
    metaDescription: "Expert anti-nuisibles Vincennes bois de Vincennes (94). Rats, souris, cafards traités. Protection logements ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Château", "Nord", "Sud"],
    problemes: ["Rats venant du bois", "Souris", "Cafards", "Punaises de lit"],
  },
  neuilly: {
    nom: "Neuilly-sur-Seine",
    region: "Hauts-de-Seine (92)",
    code: "92200",
    accroche: "Nuisibles à Neuilly ? Intervention haut de gamme et discrète.",
    description: "À Neuilly, on comprend vos exigences. Discrétion absolue, intervention soignée, résultat garanti. On traite votre problème sans que personne ne le sache.",
    metaDescription: "Dératisation désinsectisation Neuilly-sur-Seine (92). Intervention haut de gamme discrète. Hôtels particuliers, villas ☎ 07 64 07 95 35",
    quartiers: ["Sablons", "Bagatelle", "Saint-James", "Île de la Jatte"],
    problemes: ["Souris dans les hôtels particuliers", "Rats dans les jardins", "Guêpes", "Fourmis"],
  },
  saint_denis: {
    nom: "Saint-Denis",
    region: "Seine-Saint-Denis (93)",
    code: "93200",
    accroche: "Rats ou punaises à Saint-Denis ? On intervient rapidement.",
    description: "Saint-Denis, ville en pleine transformation. On intervient partout : centre historique, Pleyel, La Plaine... Même service pro pour tous.",
    metaDescription: "Désinsectisation dératisation Saint-Denis (93). Rats, cafards, punaises traités. Pleyel, Stade de France couverts ☎ 07 64 07 95 35",
    quartiers: ["Centre-ville", "Pleyel", "Stade de France", "Basilique", "La Plaine"],
    problemes: ["Rats dans les caves", "Cafards dans les immeubles", "Punaises de lit", "Souris"],
  },
};

export default function VillePage() {
  const { ville } = useParams();
  // Récupérer le path de l'URL et le normaliser
  const urlPath = window.location.pathname.replace(/^\//, "");
  const key = ville?.replace(/-/g, "_") || urlPath.replace(/-/g, "_");
  const villeSlug = key.replace(/_/g, "-");
  const seoVille = seoData.villes.find((v) => v.slug === villeSlug);
  const data = VILLES[key] || (seoVille && buildGenericVille(seoVille));
  
  // URL canonique basée sur le nom de la ville
  const canonicalPath = key.replace(/_/g, "-");

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Ville non trouvée</h1>
          <Link to="/" className="text-emerald-600 hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-16 md:pb-0">
      <Helmet>
        <title>{`Dératisation & Désinsectisation ${data.nom} (${data.code}) | Azur Tech`}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={`https://azurtech06.fr/${canonicalPath}`} />
        <meta property="og:title" content={`Dératisation Désinsectisation ${data.nom} | Azur Tech`} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={`https://azurtech06.fr/${canonicalPath}`} />
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] text-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-2 text-emerald-400 text-sm mb-4">
            <MapPin className="w-4 h-4" />
            <span>{data.region} • {data.code}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Désinsectisation & Dératisation<br />
            <span className="text-emerald-400">à {data.nom}</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mb-6">
            {data.accroche}
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="tel:0764079535" className="inline-flex items-center space-x-2 bg-white text-[#0f172a] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all">
              <Phone className="w-5 h-5" />
              <span>07 64 07 95 35</span>
            </a>
            <a href="#devis" className="inline-flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl font-semibold transition-all">
              <span>Devis gratuit</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-6 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-emerald-500" />
              <span><strong>24h/24</strong> même le week-end</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              <span><strong>Garantie</strong> résultat</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-emerald-500" />
              <span><strong>4.9/5</strong> avis clients</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">
                  Votre expert anti-nuisibles à {data.nom}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {data.description}
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: <Zap className="w-5 h-5" />, title: "Intervention rapide", desc: "Sous 2h en urgence" },
                    { icon: <Shield className="w-5 h-5" />, title: "Résultat garanti", desc: "On revient si besoin" },
                    { icon: <Users className="w-5 h-5" />, title: "Pros certifiés", desc: "Formés et équipés" },
                    { icon: <CheckCircle className="w-5 h-5" />, title: "Devis gratuit", desc: "Sans engagement" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
                      <div className="text-emerald-500">{item.icon}</div>
                      <div>
                        <p className="font-semibold text-[#0f172a] text-sm">{item.title}</p>
                        <p className="text-gray-500 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Problèmes */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">
                  Ce qu'on traite à {data.nom}
                </h2>
                <ul className="space-y-3">
                  {data.problemes.map((p, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Services */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">Nos services à {data.nom}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { icon: "🐜", nom: "Désinsectisation", slug: "desinsectisation" },
                    { icon: "🐀", nom: "Dératisation", slug: "deratisation" },
                    { icon: "🛏️", nom: "Punaises de lit", slug: "punaises-de-lit" },
                    { icon: "🐝", nom: "Guêpes / Frelons", slug: "destruction-nid-guepe-frelon" },
                    { icon: "🦠", nom: "Désinfection", slug: "desinfection" },
                  ].map((s, i) => (
                    <Link key={i} to={seoVille ? `/${s.slug}-${seoVille.slug}` : `/${s.slug}`} className="flex items-center space-x-3 p-4 bg-gray-50 hover:bg-emerald-50 rounded-xl transition-colors group">
                      <span className="text-2xl">{s.icon}</span>
                      <span className="font-medium text-[#0f172a] group-hover:text-emerald-600">{s.nom} {data.nom}</span>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 ml-auto" />
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quartiers */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">On intervient dans tout {data.nom}</h2>
                <div className="flex flex-wrap gap-2">
                  {data.quartiers.map((q, i) => (
                    <span key={i} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">{q}</span>
                  ))}
                </div>
                <p className="text-gray-500 text-sm mt-4">Et tous les autres quartiers de {data.nom} et environs.</p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28" id="devis">
                <QuoteForm ville={data.nom} />
                
                {/* Urgence */}
                <div className="mt-6 bg-[#0f172a] rounded-2xl p-6 text-white text-center">
                  <p className="text-sm text-gray-400 mb-2">Urgence à {data.nom} ?</p>
                  <a href="tel:0764079535" className="text-2xl font-bold hover:text-emerald-400 transition-colors">
                    07 64 07 95 35
                  </a>
                  <p className="text-emerald-400 text-sm mt-1">On décroche 24h/24</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-emerald-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Un problème de nuisibles à {data.nom} ?
          </h2>
          <p className="text-emerald-100 mb-6">Appelez-nous ou demandez un devis. On répond vite.</p>
          <a href="tel:0764079535" className="inline-flex items-center space-x-2 bg-white text-[#0f172a] px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all">
            <Phone className="w-5 h-5" />
            <span>07 64 07 95 35</span>
          </a>
        </div>
      </section>

      {/* Maillage interne - Autres villes */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            Nos interventions dans les Alpes-Maritimes et le Var
          </h2>
          <div className="flex flex-wrap gap-2">
            {["Nice", "Cannes", "Antibes", "Grasse", "Menton", "Cagnes-sur-Mer", "Fréjus", "Saint-Raphaël", "Mandelieu", "Mougins", "Vence", "Le Cannet", "Vallauris", "Biot"].map((ville) => {
              const slug = ville.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-");
              const isCurrentPage = ville === data.nom;
              return isCurrentPage ? (
                <span key={slug} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                  {ville}
                </span>
              ) : (
                <Link key={slug} to={`/${slug}`} className="px-3 py-1 bg-white border border-gray-200 text-gray-600 rounded-full text-sm hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-colors">
                  {ville}
                </Link>
              );
            })}
          </div>
          
          {/* Articles recommandés */}
          <h3 className="text-lg font-semibold text-gray-800 mt-8 mb-4">Articles utiles</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/blog/invasion-punaises-lit-cote-azur-2025" className="p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all">
              <span className="text-sm text-emerald-600 font-medium">Punaises de lit</span>
              <p className="text-gray-800 font-medium mt-1">Invasion de punaises sur la Côte d'Azur en 2025</p>
            </Link>
            <Link to="/blog/rats-nice-signalements-hiver-2025" className="p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all">
              <span className="text-sm text-emerald-600 font-medium">Dératisation</span>
              <p className="text-gray-800 font-medium mt-1">Rats à Nice : explosion des signalements</p>
            </Link>
            <Link to="/blog/choisir-entreprise-deratisation-nice-guide-2025" className="p-4 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all">
              <span className="text-sm text-emerald-600 font-medium">Guide</span>
              <p className="text-gray-800 font-medium mt-1">Comment choisir son dératiseur à Nice ?</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </div>
  );
}
